import { ReactNode, forwardRef } from 'react';
import { clsx } from 'clsx';

interface AvatarProps {
  src?: string;
  alt?: string;
  /** `ms` (36px) is the Flutter `CircleAvatar(radius: 18)` used in comment rows. */
  size?: 'xs' | 'sm' | 'ms' | 'md' | 'lg' | 'xl';
  /** A string renders as a bold uppercase initial; a node is rendered as given. */
  fallback?: ReactNode;
  className?: string;
}

const sizeStyles = {
  xs: 'w-6 h-6 text-label-sm',
  sm: 'w-8 h-8 text-label-md',
  ms: 'w-9 h-9 text-label-sm',
  md: 'w-10 h-10 text-body-md',
  lg: 'w-12 h-12 text-body-lg',
  xl: 'w-16 h-16 text-display-sm',
};

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ src, alt, size = 'md', fallback, className }, ref) => {
    let content: ReactNode;
    if (src) {
      content = (
        <img
          src={src}
          alt={alt || ''}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
      );
    } else if (typeof fallback === 'string') {
      const initial = (Array.from(fallback)[0] ?? '?').toUpperCase();
      content = <span className="font-bold text-lm-on-surface">{initial}</span>;
    } else {
      content = fallback || (
        <span className="text-lm-on-surface-variant" aria-hidden="true">
          ?
        </span>
      );
    }

    return (
      <div
        ref={ref}
        className={clsx(
          'avatar inline-flex items-center justify-center overflow-hidden rounded-full bg-lm-surface-container-high',
          sizeStyles[size],
          className
        )}
      >
        {content}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';
